/**
 * TeamsTab - Ultra Modern Minimalist Design
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Plus, ArrowUpRight } from 'lucide-react';

const translations = {
  en: {
    teams: 'Teams',
    subtitle: 'Organize your workforce',
    create: 'Add',
    members: 'members'
  },
  no: {
    teams: 'Team',
    subtitle: 'Organiser ansatte',
    create: 'Legg til',
    members: 'medlemmer'
  }
};

// Extended mock teams
const mockTeams = [
  { 
    id: 't1', 
    name: 'M&A Advisory', 
    description: 'Mergers and acquisitions',
    members: [
      { name: 'Sarah Chen', img: 'https://i.pravatar.cc/150?img=32' },
      { name: 'James Wilson', img: 'https://i.pravatar.cc/150?img=33' },
      { name: 'Emma Davis', img: 'https://i.pravatar.cc/150?img=25' },
      { name: 'Michael Brown', img: 'https://i.pravatar.cc/150?img=12' },
      { name: 'Lisa Anderson', img: 'https://i.pravatar.cc/150?img=44' },
    ]
  },
  { 
    id: 't2', 
    name: 'Corporate Governance', 
    description: 'Board advisory and compliance',
    members: [
      { name: 'David Kim', img: 'https://i.pravatar.cc/150?img=52' },
      { name: 'Anna Berg', img: 'https://i.pravatar.cc/150?img=23' },
      { name: 'Thomas Eriksen', img: 'https://i.pravatar.cc/150?img=59' },
      { name: 'Sophie Miller', img: 'https://i.pravatar.cc/150?img=20' },
    ]
  },
  { 
    id: 't3', 
    name: 'Commercial Litigation', 
    description: 'Dispute resolution',
    members: [
      { name: 'Robert Taylor', img: 'https://i.pravatar.cc/150?img=60' },
      { name: 'Jennifer Lee', img: 'https://i.pravatar.cc/150?img=47' },
      { name: 'Mark Johnson', img: 'https://i.pravatar.cc/150?img=68' },
    ]
  },
  { 
    id: 't4', 
    name: 'Employment Advisory', 
    description: 'HR and labor law',
    members: [
      { name: 'Karen White', img: 'https://i.pravatar.cc/150?img=41' },
      { name: 'Peter Hansen', img: 'https://i.pravatar.cc/150?img=14' },
      { name: 'Linda Moore', img: 'https://i.pravatar.cc/150?img=9' },
      { name: 'John Smith', img: 'https://i.pravatar.cc/150?img=3' },
      { name: 'Amy Chen', img: 'https://i.pravatar.cc/150?img=5' },
      { name: 'Chris Park', img: 'https://i.pravatar.cc/150?img=8' },
    ]
  },
  { 
    id: 't5', 
    name: 'Real Estate Transactions', 
    description: 'Property and development',
    members: [
      { name: 'Daniel Garcia', img: 'https://i.pravatar.cc/150?img=11' },
      { name: 'Michelle Wong', img: 'https://i.pravatar.cc/150?img=16' },
      { name: 'Alex Turner', img: 'https://i.pravatar.cc/150?img=53' },
    ]
  },
  { 
    id: 't6', 
    name: 'Tech & IP', 
    description: 'Technology and intellectual property',
    members: [
      { name: 'Ryan Cooper', img: 'https://i.pravatar.cc/150?img=57' },
      { name: 'Nina Patel', img: 'https://i.pravatar.cc/150?img=26' },
      { name: 'Steve Martinez', img: 'https://i.pravatar.cc/150?img=61' },
      { name: 'Emily Zhang', img: 'https://i.pravatar.cc/150?img=28' },
    ]
  },
  { 
    id: 't7', 
    name: 'Banking & Finance', 
    description: 'Financial transactions',
    members: [
      { name: 'Oliver Scott', img: 'https://i.pravatar.cc/150?img=15' },
      { name: 'Rachel Green', img: 'https://i.pravatar.cc/150?img=24' },
      { name: 'Jake Williams', img: 'https://i.pravatar.cc/150?img=51' },
      { name: 'Mia Thompson', img: 'https://i.pravatar.cc/150?img=29' },
      { name: 'Lucas Hall', img: 'https://i.pravatar.cc/150?img=56' },
    ]
  },
  { 
    id: 't8', 
    name: 'Tax Advisory', 
    description: 'Tax planning and compliance',
    members: [
      { name: 'Grace Liu', img: 'https://i.pravatar.cc/150?img=31' },
      { name: 'Ben Carter', img: 'https://i.pravatar.cc/150?img=58' },
    ]
  },
];

interface TeamsTabProps {
  teams: any[];
  users: any[];
  onCreate: () => void;
  onRefresh: () => void;
  language?: string;
}

const TeamsTab: React.FC<TeamsTabProps> = ({ teams: propTeams, users, onCreate, language = 'en' }) => {
  const t = translations[language] || translations.en;
  
  // Always show all 8 mock teams for demo purposes
  const teams = mockTeams.map((mock, i) => {
    const realTeam = propTeams[i];
    return realTeam ? { ...mock, id: realTeam.id } : mock;
  });

  const getMembers = (team: any) => {
    return team.members || [];
  };

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">{t.teams}</h1>
          <p className="text-gray-400 text-sm mt-0.5">{t.subtitle}</p>
        </div>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          onClick={onCreate}
          className="h-9 px-4 bg-gray-900 text-white rounded-lg text-sm font-medium flex items-center gap-1.5 hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-4 h-4" />
          {t.create}
        </motion.button>
      </div>

      {/* Teams List */}
      <div className="space-y-2">
        {teams.map((team, index) => {
          const members = getMembers(team);
          
          return (
            <motion.div
              key={team.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className="group bg-white border border-gray-100 hover:border-gray-200 rounded-xl px-4 py-3.5 cursor-pointer transition-all hover:shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  {/* Stacked avatars */}
                  <div className="flex -space-x-2 flex-shrink-0">
                    {members.slice(0, 4).map((member: any, i: number) => (
                      <img
                        key={i}
                        src={member.img}
                        alt={member.name}
                        className="w-8 h-8 rounded-full border-2 border-white object-cover"
                        title={member.name}
                      />
                    ))}
                    {members.length > 4 && (
                      <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-xs font-medium text-gray-500">
                        +{members.length - 4}
                      </div>
                    )}
                  </div>

                  {/* Team info */}
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900 text-[15px]">{team.name}</h3>
                    <p className="text-sm text-gray-400">{team.description}</p>
                  </div>
                </div>

                {/* Right side */}
                <div className="flex items-center gap-3 ml-4">
                  <span className="text-sm text-gray-400">{members.length} {t.members}</span>
                  <ArrowUpRight className="w-4 h-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default TeamsTab;
